<template>
  <v-navigation-drawer
    v-model="show"
    v-lock-scroll="show"
    app
    temporary
    right
    width="1000px"
    class="right-side-panel the-failed-egift-sends-panel"
  >
    <panel-header :show.sync="show">
      {{ showSuccessArea ? '' : 'Review and resolve failed eGift sends' }}
    </panel-header>

    <div v-if="show" class="right-side-panel__content-wrapper">
      <cg-loader v-if="loading" style="padding: 30px 0" />

      <!-- SUCCESS -->
      <div v-else-if="showSuccessArea" class="the-failed-egift-sends-panel__success">
        <icons-thumb-up :width="32" :height="32" />
        <div class="the-failed-egift-sends-panel__success-title">Thank you!</div>
        <div>Payment successful. Your failed eGift sends are now being processed</div>
        <div v-if="orderNumbersCompleted.length">
          <div class="the-failed-egift-sends-panel__success-title">Order IDs:</div>
          <div>{{ orderNumbersCompleted.join(', ') }}</div>
        </div>
        <div>
          You can see the updated invoices in
          <a :href="ordersPageUrl" target="_blank" rel="noopener">My Orders</a> dashboard
        </div>
      </div>

      <!-- LIST -->
      <div v-else class="the-failed-egift-sends-panel__content">
        <div>
          <strong>{{ failedSends.length }}</strong> automated {{ failedSends.length === 1 ? 'send has' : 'sends have' }}
          failed, due to insufficient balance in payment method.
        </div>

        <div class="the-failed-egift-sends-panel__list">
          <div v-if="selectedSends.length > 1 && !isRemoving">
            <span class="the-failed-egift-sends-panel__link" @click="removeSends(selectedSends)">
              Remove recipients
            </span>
          </div>

          <cg-loader v-if="isRemoving" style="padding: 10px 0" />
          <v-data-table
            v-else
            v-model="selectedSends"
            :headers="headers"
            :items="failedSends"
            :items-per-page="failedSends.length || 9999"
            item-key="id"
            show-select
            disable-sort
            hide-default-footer
            class="the-failed-egift-sends-panel__table"
          >
            <template #[`item.recipient`]="{ value }">
              <strong>{{ getRecipientFullName(value) }}</strong>
            </template>
            <template #[`item.campaign`]="{ value }">
              {{ value || '-' }}
            </template>
            <template #[`item.original_send_date`]="{ value }">
              {{ value || '-' }}
            </template>
            <template #[`item.payment_method`]="{ value }">
              {{ getPaymentMethodLabel(value) }}
            </template>
            <template #[`item.remove`]="{ item }">
              <span class="the-failed-egift-sends-panel__link" @click="removeSends([item])">
                Remove
              </span>
            </template>
          </v-data-table>
        </div>

        <div class="the-failed-egift-sends-panel__summary">
          <div class="the-failed-egift-sends-panel__summary-title">
            Send eGift to selected {{ selectedSends.length }}
            {{ selectedSends.length === 1 ? 'recipient' : 'recipients' }}
          </div>
          <cg-loader v-if="isCalculating || isSending" style="padding: 10px 0" />
          <div v-else class="the-failed-egift-sends-panel__summary-details">
            <div class="the-failed-egift-sends-panel__summary-row">
              <span>Max gift cost</span>
              <span>{{ prices.subtotal | priceFilter }}</span>
            </div>
            <div class="the-failed-egift-sends-panel__summary-row">
              <span>Max shipping cost</span>
              <span>{{ prices.shipping | priceFilter }}</span>
            </div>
            <div class="the-failed-egift-sends-panel__summary-row">
              <span>Deposit</span>
              <span>{{ prices.total | priceFilter }}</span>
            </div>
          </div>
        </div>

        <payment-module
          :paymentMethod.sync="payment.method"
          :paymentData.sync="payment.paymentData"
          :splitPaymentData.sync="payment.splitPaymentData"
          :defaultPaymentMethod="payment.method"
          :amount="prices.total"
          use-split-payment-if-available
          ref="paymentModule"
        />

        <div>
          <cg-button :disabled="isSendDisabled" @click="handleSend">Send eGift</cg-button>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import axios from 'axios';
import panelVModel from '@/mixins/panelVModel';
import PanelHeader from '@/components/PanelHeader.vue';
import PaymentModule from '@/components/payment/PaymentModule.vue';
import PaymentType from '@/components/payment/paymentModule/utils/PaymentType';
import getPaymentBody from '@/components/payment/paymentModule/utils/getPaymentBody';
import { CgButton, CgLoader } from '@corporategiftcom/design-system';
import { FailedSendsService, FAILED_SEND_KINDS } from '@/services/myCampaign/automationFailure/failedSendService';
import { FailedSendsPayloadBuilder } from '@/services/myCampaign/automationFailure/payloadBuilder';

const TABLE_HEADERS = Object.freeze([
  { text: 'Recipient', value: 'recipient', width: 200 },
  { text: 'Campaign', value: 'campaign' },
  { text: 'Original date send', value: 'original_send_date' },
  { text: 'Payment method', value: 'payment_method' },
  { text: 'Remove', value: 'remove' },
]);

const EMPTY_PRICES = Object.freeze({ subtotal: 0, shipping: 0, total: 0 });

// Failed automated eGift sends (failed payment) of all campaigns the user may resolve.
export default {
  name: 'TheFailedEgiftSendsPanel',
  components: {
    PanelHeader,
    PaymentModule,
    CgButton,
    CgLoader,
  },
  mixins: [panelVModel],
  data: () => ({
    loading: false,
    isCalculating: false,
    isSending: false,
    isRemoving: false,
    // The prices couldn't be calculated: don't offer a send priced at $0.
    pricesError: false,
    showSuccessArea: false,
    failedSends: [],
    selectedSends: [],
    prices: { ...EMPTY_PRICES },
    orderNumbersCompleted: [],
    payment: {
      method: PaymentType.BE,
      paymentData: null,
      splitPaymentData: null,
    },
    headers: TABLE_HEADERS,
    calculateController: null,
  }),
  computed: {
    isSendDisabled() {
      return (
        !this.selectedSends.length ||
        this.isCalculating ||
        this.isSending ||
        this.isRemoving ||
        this.pricesError
      );
    },
    ordersPageUrl() {
      return `${window.location.origin}/orders`;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.getFailedSends();
        return;
      }
      // A resend changes the counts on the page.
      if (this.showSuccessArea) this.$emit('fetchData');
      this.calculateController?.abort();
      Object.assign(this.$data, this.$options.data());
    },
    selectedSends(sends) {
      if (!this.show) return;
      this.calculatePrices(sends);
    },
  },
  methods: {
    async getFailedSends() {
      this.loading = true;
      try {
        this.failedSends = await FailedSendsService.getFailedActions(FAILED_SEND_KINDS.EGIFT);
      } catch (e) {
        this.$cgToast.error(e.response?.data?.message || 'Failed to load data');
      } finally {
        this.loading = false;
      }
    },
    async calculatePrices(sends) {
      // Only the latest selection's costs matter.
      this.calculateController?.abort();
      if (!sends.length) {
        this.calculateController = null;
        this.isCalculating = false;
        this.pricesError = false;
        this.prices = { ...EMPTY_PRICES };
        return;
      }

      const controller = new AbortController();
      this.calculateController = controller;
      this.isCalculating = true;
      try {
        const { success, data } = await FailedSendsService.postCalculatePrices(
          FailedSendsPayloadBuilder.buildDismissPayload(sends),
          controller.signal,
        );
        // Never fall back to $0 prices, the panel would offer a "free" resend.
        if (!success || typeof data?.total !== 'number') throw new Error('Failed to calculate prices');
        this.prices = {
          subtotal: data.subtotal || 0,
          shipping: data.shipping || 0,
          total: data.total,
        };
        this.pricesError = false;
      } catch (e) {
        if (axios.isCancel(e) || this.calculateController !== controller) return;
        this.prices = { ...EMPTY_PRICES };
        this.pricesError = true;
        this.$cgToast.error(e.response?.data?.message || 'Failed to calculate prices');
      } finally {
        if (this.calculateController === controller) this.isCalculating = false;
      }
    },
    async removeSends(sends) {
      if (!sends.length || this.isRemoving) return;
      this.isRemoving = true;
      const ids = sends.map(({ id }) => id);
      try {
        await FailedSendsService.postDismissActions(FailedSendsPayloadBuilder.buildDismissPayload(sends));
        this.selectedSends = this.selectedSends.filter(({ id }) => !ids.includes(id));
        this.failedSends = this.failedSends.filter(({ id }) => !ids.includes(id));
        this.$emit('fetchData');
        this.$cgToast.successBold(
          `${ids.length} ${ids.length === 1 ? 'recipient' : 'recipients'} removed successfully!`,
        );
      } catch (e) {
        this.$cgToast.error(e.response?.data?.message || 'Failed to remove recipients. Please try again.');
      } finally {
        this.isRemoving = false;
      }
    },
    async handleSend() {
      if (this.isSendDisabled) return;
      if (!(await this.$refs.paymentModule?.validate())) {
        this.$cgToast.error('The requested amount is higher than limit per order for this budget entity.');
        return;
      }

      this.isSending = true;
      try {
        const { method, paymentData, splitPaymentData } = this.payment;
        const payment = getPaymentBody({ method, paymentData, allowSplitPayment: true, splitPaymentData });
        const response = await FailedSendsService.postFailedActions({
          ...FailedSendsPayloadBuilder.buildDismissPayload(this.selectedSends),
          payment: Array.isArray(payment) ? payment : [payment],
        });
        // Closed while sending: the state was already reset, just refresh the page counts.
        if (!this.show) {
          this.$emit('fetchData');
          return;
        }
        if (!response?.success || !Array.isArray(response.data)) {
          throw new Error('Failed to send orders. Please try again.');
        }
        this.orderNumbersCompleted = response.data
          .filter((item) => item?.status === 'completed' && item?.order_number != null)
          .map((item) => item.order_number);
        this.showSuccessArea = true;
      } catch (e) {
        this.$cgToast.error(e.response?.data?.message || e.message || 'Failed to send orders. Please try again.');
      } finally {
        this.isSending = false;
      }
    },
    getRecipientFullName(recipient) {
      return `${recipient?.first_name || ''} ${recipient?.last_name || ''}`.trim() || '-';
    },
    getPaymentMethodLabel(paymentMethod) {
      if (!paymentMethod) return '-';
      if (paymentMethod.method === PaymentType.BE) return `${paymentMethod.budgetEntity} budget entity`;
      if (paymentMethod.method === PaymentType.CC) return `card ending with ${paymentMethod.cardNumber}`;
      return paymentMethod.name || '-';
    },
  },
};
</script>

<style lang="scss">
.the-failed-egift-sends-panel__table {
  .v-data-table-header th:first-child,
  tbody td:first-child {
    width: 48px !important;
    min-width: 48px !important;
    max-width: 48px !important;
  }

  tbody td:nth-child(2) {
    min-width: 200px;
  }

  .v-data-table-header tr th {
    border-right: unset !important;
  }

  .v-data-table-header tr th span {
    text-transform: uppercase;
  }

  .v-data-table__selected,
  .v-data-table__selected td:first-child {
    background: #f6f5ff !important;
  }

  .mdi-checkbox-marked,
  .v-data-table-header .mdi-minus-box::before {
    color: #000000 !important;
  }
}
</style>

<style lang="scss" scoped>
@import '@/css/common/right-side-panel.scss';

.the-failed-egift-sends-panel {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    font-size: 16px;
    line-height: 24px;
    color: #000000;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__link {
    color: #6e50ff;
    font-weight: 600;
    cursor: pointer;
  }

  &__summary {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 300px;

    &-title {
      font-weight: 700;
    }

    &-details {
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 16px 0;
      border-top: 1px solid #d3d2d2;
      border-bottom: 1px solid #d3d2d2;
      font-size: 15px;
      line-height: 22px;
    }

    &-row {
      display: flex;
      justify-content: space-between;

      &:last-child {
        font-weight: 600;
      }
    }
  }

  &__success {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 40px;
    text-align: center;
    color: #000000;

    &-title {
      font-weight: 700;
      font-size: 20px;
    }

    a {
      color: #6e50ff;
      font-weight: 700;
    }
  }

  .cg-button {
    border-radius: 100px;
  }
}
</style>
